import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import {
    Grid,
    Dialog,
    DialogActions,
    DialogContent,
    TextField,
    MenuItem,
    Checkbox,
    FormGroup,
    FormControlLabel,
    DialogTitle,
    TablePagination,
    CircularProgress,
    IconButton,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { saveFilter } from '../../actions/filterActions';
import { savePage } from '../../actions/pageActions';
import ViewResult from './ViewResult';
import ImagesUpload from '../pages/Upload';
import TaskCard from './TaskCard';
import { useParams } from 'react-router-dom';
import { HV_THEME, HVHeader, HVButton, Card, Icons } from '../shared/DesignSystem';

const OpenBenchmark = (props) => {
    const { benchmarkId } = useParams();
    const dispatch = useDispatch();
    const pageNumber = useSelector((state) => state.page.pageNumber);
    const pageSize = useSelector((state) => state.page.pageSize);

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('');
    const [results, setResults] = useState({});
    const [validationResult, setValidationResult] = useState('');
    const [type, setType] = useState('extraction');
    const [openUpload, setOpenUpload] = useState(false);
    const [openResult, setOpenResult] = useState(false);
    const [openBenchmarkConfig, setOpenBenchmarkConfig] = useState(false);
    const [benchmarkConfig, setBenchmarkConfig] = useState({});
    const [uploadType, setUploadType] = useState('');
    const [images, setImages] = useState([]);
    const [completedInference, setCompletedInference] = useState(0);
    const [completedOcr, setCompletedOcr] = useState(0);
    const [completedAnnotations, setCompletedAnnotations] = useState(0);
    const [customResult, setCustomResult] = useState({});
    const [totalSamples, setTotalSamples] = useState(0);
    const [flags, setFlags] = useState({});
    const [responseFlagType, setResponseFlagType] = useState('');
    const [annotationFlagType, setAnnotationFlagType] = useState('');
    const [modelName, setModelName] = useState('');
    const [modelNameOpen, setModelNameOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const [benchmarkDetails, setBenchmarkDetails] = useState(props.location.state || {
        benchmark: benchmarkId, id: '', name: '', benchmarkType: '', ocrType: '',
    });

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    useEffect(() => {
        setPage(pageNumber);
        setRowsPerPage(pageSize);
    }, []);

    const offset = page * rowsPerPage;
    const currentPageData = images
        .slice(offset, offset + rowsPerPage)
        .map((image, index) => {
            const imageId = image.imageKey.split('/')[3].split('.')[0];
            const toCheck = responseFlagType.length || annotationFlagType.length;
            const card = (
                <TaskCard
                    key={image.imageKey}
                    image={image}
                    index={index}
                    handleAnnotate={handleAnnotate}
                    handleViewAnnotation={handleViewAnnotation}
                    handleResponse={handleResponse}
                    projectType={type}
                    getImages={getImages}
                    flags={flags}
                />
            );
            if (!toCheck) return card;
            if (responseFlagType.length && annotationFlagType.length) {
                return (flags[imageId]?.[responseFlagType] && flags[imageId]?.[annotationFlagType]) ? card : '';
            }
            if (responseFlagType.length) return flags[imageId]?.[responseFlagType] ? card : '';
            return flags[imageId]?.[annotationFlagType] ? card : '';
        });

    const handleChangePage = (event, newPage) => {
        dispatch(savePage(newPage, rowsPerPage));
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        dispatch(savePage(page, event.target.value));
        setRowsPerPage(parseInt(event.target.value));
        setPage(0);
    };

    useEffect(() => {
        let inferenceCount = 0, ocrCount = 0, annotationCount = 0;
        images.forEach(image => {
            if (image.inference) inferenceCount++;
            if (image.ocr) ocrCount++;
            if (image.annotation) annotationCount++;
        });
        setCompletedInference(inferenceCount);
        setCompletedOcr(ocrCount);
        setCompletedAnnotations(annotationCount);
    }, [images]);

    function getType(id) {
        axios.post('/api/projects/status', { projectId: id })
            .then((res) => { if (res.data.projectType) setType(res.data.projectType); })
            .catch((e) => enqueueSnackbar(e.response?.data, { variant: 'error' }));
    }

    function getStatus() {
        axios.post('/api/benchmarks/status', { benchmark: benchmarkId })
            .then((res) => {
                if (res.data.projectType) setType(res.data.projectType);
                setStatus(res.data.status);
                setBenchmarkConfig(res.data.config);
                setModelName(res.data.default_model);
                if (res.data.results) {
                    setResults(res.data.results);
                    setCustomResult(res.data.results.custom);
                    setTotalSamples(res.data.results.total);
                }
                if (res.data.validationResult) setValidationResult(res.data.validationResult);
                if (!benchmarkDetails.name) {
                    setBenchmarkDetails({
                        benchmark: benchmarkId, id: res.data.id, name: res.data.name,
                        benchmarkType: res.data.benchmarkType, ocrType: res.data.ocrType, docType: res.data.docType,
                    });
                    if (res.data.benchmarkType !== 'group') getType(res.data.id);
                }
            })
            .catch((e) => enqueueSnackbar(e.response?.data, { variant: 'error' }));
    }

    function refreshData() { getImages(); getStatus(); }

    useEffect(() => {
        getStatus();
        if (benchmarkDetails.id && benchmarkDetails.benchmarkType !== 'group') getType(benchmarkDetails.id);
        dispatch(saveFilter({}, ''));
    }, [benchmarkId]);

    async function getImages() {
        try {
            setLoading(true);
            const response = await axios.post('/api/benchmarks/images/list', { benchmarkId });
            if (response?.data) {
                response.data.images.sort((a, b) =>
                    Number(a.imageKey.split('/').pop().split('.')[0]) > Number(b.imageKey.split('/').pop().split('.')[0]) ? 1 : -1
                );
                setImages(response.data.images);
            }
        } catch (e) {
            enqueueSnackbar('Error fetching images', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }

    const getFlags = async () => {
        try {
            const res = await axios.post('/api/benchmarks/flags/view', { benchmark: benchmarkId });
            setFlags(res.data);
        } catch (err) { console.log('Get flags error', err); }
    };

    useEffect(() => { getImages(); getFlags(); }, [benchmarkId]);

    useEffect(() => {
        let intervalId;
        if (status === 'model_generating') intervalId = 0;
        return () => clearInterval(intervalId);
    }, [status]);

    function handleAnnotate(imageKey) {
        props.history.push({
            pathname: `/benchmark/annotate/${benchmarkId}/${encodeURIComponent(imageKey)}`,
            state: { ...benchmarkDetails, imageId: imageKey.split('/').pop().split('.')[0], imageKey, projectType: type, type: 'new' },
        });
    }
    function handleViewAnnotation(imageKey) {
        const annotationList = images.filter(o => o.annotation).map(o => o.imageKey);
        props.history.push({
            pathname: `/benchmark/viewAnnotation/${benchmarkId}/${encodeURIComponent(imageKey)}`,
            state: { ...benchmarkDetails, imageKey, images, annotationList, projectType: type, type: 'new' },
        });
    }
    function handleResponse(imageKey) {
        const inferenceList = images.filter(o => o.inference).map(o => o.imageKey);
        props.history.push({
            pathname: `/benchmark/viewResponse/${benchmarkId}/${encodeURIComponent(imageKey)}`,
            state: { ...benchmarkDetails, imageKey, images, inferenceList, projectType: type },
        });
    }

    const handleHTModel = () => {
        axios.post('api/benchmarks/inference', {
            benchmark: benchmarkId, id: benchmarkDetails.id,
            ocrType: benchmarkDetails.ocrType, type: benchmarkDetails.benchmarkType, modelName,
        })
            .then(res => enqueueSnackbar(res.data, { variant: 'success' }))
            .catch(() => enqueueSnackbar('Error', { variant: 'error' }));
    };

    const handleBenchmark = () => {
        enqueueSnackbar('Running Benchmark', { variant: 'success' });
        setResults({}); setValidationResult('');
        axios.post('api/benchmarks/run', { benchmark: benchmarkId, type })
            .then(() => getStatus())
            .catch(() => enqueueSnackbar('Error! Try later', { variant: 'error' }));
    };

    const handleSetBenchmarkConfig = () => {
        axios.post('api/benchmarks/setConfig', { benchmark: benchmarkId, config: benchmarkConfig })
            .then(() => { enqueueSnackbar('Config Updated', { variant: 'success' }); window.location.reload(); })
            .catch(e => enqueueSnackbar(e.response?.data, { variant: 'error' }));
    };

    const saveModelName = async () => {
        try {
            await axios.post('api/benchmarks/setDefaultModel', { modelName, benchmark: benchmarkId });
            enqueueSnackbar('Default model updated', { variant: 'success' });
            setModelNameOpen(false);
        } catch { enqueueSnackbar('Error setting default model', { variant: 'error' }); }
    };

    const handleGoBack = () => {
        props.history.push({
            pathname: benchmarkDetails.benchmarkType === 'project'
                ? `/project/benchmarks/${benchmarkDetails.id}`
                : `/group/benchmarks/${benchmarkDetails.id}`,
            state: { id: benchmarkDetails.id, name: '', docType: benchmarkDetails.docType, benchmarkCategory: benchmarkDetails.benchmarkType },
        });
    };

    const handleQc = () => {
        props.history.push({ pathname: `/benchmark/qc/list/${benchmarkId}`, state: { ...benchmarkDetails, type } });
    };

    const { state } = props.location;
    if (state === undefined) return <Redirect to='/' />;

    const resultsReady = status === 'benchmark_completed' && (Object.keys(results).length > 0 || Object.keys(validationResult).length > 0);
    const isProject = benchmarkDetails.benchmarkType === 'project';
    const categoryLabel = isProject ? 'Projects' : 'Groups';
    const tabId = isProject ? 'projects' : 'groups';

    // Stats
    const stats = [
        { label: 'Inference', value: completedInference, total: images.length },
        { label: 'OCR', value: completedOcr, total: images.length },
        { label: 'Annotation', value: completedAnnotations, total: images.length },
    ];

    return (
        <div style={{ background: HV_THEME.bg, minHeight: '100vh', paddingBottom: 60, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: HV_THEME.ink }}>
            <HVHeader
                tab={tabId}
                showTabs={false}
                role={{ name: 'Admin', initials: 'AD', color: '#e6194b', label: 'Admin' }}
                env="STAGING"
                onLogout={() => props.history.push('/login')}
            />

            {/* Breadcrumb bar */}
            <div style={{ background: '#fff', borderBottom: '1px solid #ececec', padding: '14px 40px' }}>
                <div style={{ maxWidth: 1440, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 14 }}>
                    <HVButton variant="ghost" size="sm" icon={<Icons.arrowL size={14} />} onClick={handleGoBack}>Back</HVButton>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: HV_THEME.mute }}>
                        <span style={{ cursor: 'pointer' }} onClick={() => props.history.push('/')}>{categoryLabel}</span>
                        <span>/</span>
                        <span style={{ cursor: 'pointer' }} onClick={handleGoBack}>Benchmarks</span>
                        <span>/</span>
                        <span style={{ color: HV_THEME.ink, fontWeight: 600 }}>{benchmarkDetails.name || benchmarkId}</span>
                    </div>
                </div>
            </div>

            <div style={{ padding: '24px 40px', maxWidth: 1440, margin: '0 auto' }}>

                {/* Page title + stats row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 16 }}>
                    <div>
                        <div style={{ fontSize: 10, letterSpacing: '0.18em', fontWeight: 700, color: HV_THEME.mute, textTransform: 'uppercase' }}>Benchmark</div>
                        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '4px 0 2px', letterSpacing: '-0.02em' }}>{benchmarkDetails.name || benchmarkId}</h1>
                        <div style={{ fontSize: 11, color: HV_THEME.mute, fontFamily: "'JetBrains Mono', monospace" }}>{benchmarkId}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        {stats.map(s => (
                            <div key={s.label} style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 4, padding: '10px 18px', textAlign: 'center', minWidth: 90 }}>
                                <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>{s.value}<span style={{ fontSize: 12, fontWeight: 400, color: HV_THEME.mute }}>/{s.total}</span></div>
                                <div style={{ fontSize: 10, letterSpacing: '0.1em', fontWeight: 600, color: HV_THEME.mute, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Action groups */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>

                    {/* Group 1: Navigation & Data */}
                    <Card pad={0} style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 700, color: HV_THEME.mute, textTransform: 'uppercase', marginBottom: 10 }}>Data</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <HVButton variant="ghost" size="sm" icon={<Icons.refresh size={13} />} onClick={refreshData}>Refresh</HVButton>
                            <HVButton variant="secondary" size="sm" icon={<Icons.upload size={13} />} onClick={() => { setUploadType('folder'); setOpenUpload(true); }}>Upload Folder</HVButton>
                            <HVButton variant="secondary" size="sm" icon={<Icons.image size={13} />} onClick={() => { setUploadType('files'); setOpenUpload(true); }}>Upload Images</HVButton>
                        </div>
                    </Card>

                    {/* Group 2: Filters */}
                    <Card pad={0} style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 700, color: HV_THEME.mute, textTransform: 'uppercase', marginBottom: 10 }}>Filters</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <HVButton
                                size="sm"
                                variant={annotationFlagType === 'annotation' ? 'accent' : 'ghost'}
                                icon={<Icons.flag size={13} />}
                                onClick={() => annotationFlagType === 'annotation' ? setAnnotationFlagType('') : setAnnotationFlagType('annotation')}
                            >
                                {annotationFlagType === 'annotation' ? 'Clear Annotation Filter' : 'Flagged Annotations'}
                            </HVButton>
                            <HVButton
                                size="sm"
                                variant={responseFlagType === 'response' ? 'accent' : 'ghost'}
                                icon={<Icons.flag size={13} />}
                                onClick={() => responseFlagType === 'response' ? setResponseFlagType('') : setResponseFlagType('response')}
                            >
                                {responseFlagType === 'response' ? 'Clear Response Filter' : 'Flagged Responses'}
                            </HVButton>
                        </div>
                    </Card>

                    {/* Group 3: Configuration */}
                    {(isProject && type !== 'validation') || isProject ? (
                        <Card pad={0} style={{ padding: '12px 16px' }}>
                            <div style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 700, color: HV_THEME.mute, textTransform: 'uppercase', marginBottom: 10 }}>Configuration</div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {isProject && type !== 'validation' && (
                                    <HVButton size="sm" variant="secondary" icon={<Icons.cog size={13} />} onClick={() => setModelNameOpen(true)}>Set Default Model</HVButton>
                                )}
                                {isProject && (
                                    <HVButton size="sm" variant="secondary" icon={<Icons.cog size={13} />} onClick={() => setOpenBenchmarkConfig(true)}>Edit Benchmark Config</HVButton>
                                )}
                            </div>
                        </Card>
                    ) : null}

                    {/* Group 4: Run Actions + Results */}
                    <Card pad={0} style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 700, color: HV_THEME.mute, textTransform: 'uppercase', marginBottom: 10 }}>Actions</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <HVButton size="sm" variant="primary" icon={<Icons.spark size={13} />} onClick={handleHTModel}>Run Inference</HVButton>
                            <HVButton size="sm" variant="primary" icon={<Icons.beaker size={13} />} onClick={handleBenchmark}>Run Benchmark</HVButton>
                            <div style={{ width: 1, height: 28, background: '#e0e0e0', margin: '0 4px' }} />
                            <HVButton size="sm" variant="secondary" icon={<Icons.doc size={13} />} disabled={!resultsReady} onClick={() => setOpenResult(true)}>View Results</HVButton>
                            <HVButton size="sm" variant="secondary" icon={<Icons.check size={13} />} disabled={!resultsReady} onClick={handleQc}>View QC</HVButton>
                        </div>
                    </Card>
                </div>

                {/* Pagination + image grid */}
                <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 4 }}>
                    <div style={{ borderBottom: '1px solid #ececec', padding: '4px 8px' }}>
                        <TablePagination
                            component="div"
                            count={images.length}
                            page={page}
                            onChangePage={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 25, 50, 100, 150, 200, 250, 300, 400, 500]}
                        />
                    </div>
                    <div style={{ padding: '16px 8px' }}>
                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                                <CircularProgress />
                            </div>
                        ) : images.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: HV_THEME.mute }}>
                                <Icons.image size={32} stroke={HV_THEME.mute} />
                                <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600, color: HV_THEME.ink }}>No images uploaded yet</div>
                                <div style={{ fontSize: 13, marginTop: 4 }}>Upload a folder or images to get started.</div>
                            </div>
                        ) : (
                            <Grid container spacing={2} style={{ marginLeft: 8 }}>
                                {currentPageData}
                            </Grid>
                        )}
                    </div>
                </div>
            </div>

            {/* Upload dialog */}
            <Dialog open={openUpload} onClose={() => setOpenUpload(false)} aria-labelledby='upload' PaperProps={{ style: { position: 'relative' } }}>
                <IconButton onClick={() => setOpenUpload(false)} style={{ position: 'absolute', right: 8, top: 8, zIndex: 10, background: 'rgba(255,255,255,0.9)' }}>
                    <Icons.x size={18} />
                </IconButton>
                <ImagesUpload
                    projectId={benchmarkDetails.id}
                    groupId={benchmarkDetails.group}
                    type={uploadType}
                    ocrType={benchmarkDetails.ocrType}
                    benchmark={benchmarkId}
                    benchmarkType={benchmarkDetails.benchmarkType}
                    projectType={type}
                    docType={benchmarkDetails.docType}
                    uploadData='benchmark'
                    getImages={getImages}
                    setOpenUpload={setOpenUpload}
                />
            </Dialog>

            {/* Results dialog */}
            {(Object.keys(results).length > 0 || Object.keys(validationResult).length > 0) && (
                <Dialog open={openResult} onClose={() => setOpenResult(false)} aria-labelledby='benchmark' maxWidth='xl' PaperProps={{ style: { position: 'relative' } }}>
                    <IconButton onClick={() => setOpenResult(false)} style={{ position: 'absolute', right: 8, top: 8, zIndex: 10, background: 'rgba(255,255,255,0.9)' }}>
                        <Icons.x size={18} />
                    </IconButton>
                    <DialogContent>
                        <ViewResult
                            results={results}
                            totalSamples={totalSamples}
                            validationResult={validationResult}
                            benchmarkType={benchmarkDetails.benchmarkType}
                            benchmark={benchmarkId}
                            type={type}
                            getStatus={getStatus}
                            customResult={customResult}
                            setCustomResult={setCustomResult}
                        />
                    </DialogContent>
                </Dialog>
            )}

            {/* Benchmark config dialog */}
            <Dialog open={openBenchmarkConfig} onClose={() => setOpenBenchmarkConfig(false)} aria-labelledby='benchmark-config' maxWidth='xl'>
                <DialogTitle style={{ position: 'relative', paddingRight: 48 }}>
                    Edit Benchmark Config
                    <IconButton onClick={() => setOpenBenchmarkConfig(false)} style={{ position: 'absolute', right: 8, top: 8 }}>
                        <Icons.x size={18} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {benchmarkConfig && Object.keys(benchmarkConfig).map((field) => (
                        <div key={field} style={{ marginBottom: 10 }}>
                            <TextField name='field' label='Field Name' variant='outlined' value={field} disabled />
                            <span>&nbsp;</span>
                            <TextField name='threshold' label='Set Threshold' variant='outlined' value={benchmarkConfig[field].threshold} onChange={e => setBenchmarkConfig({ ...benchmarkConfig, [field]: { ...benchmarkConfig[field], threshold: e.target.value } })} />
                            <span>&nbsp;</span>
                            <TextField name='characters' label='Remove characters' variant='outlined' value={benchmarkConfig[field].characters} onChange={e => setBenchmarkConfig({ ...benchmarkConfig, [field]: { ...benchmarkConfig[field], characters: e.target.value } })} />
                            <span>&nbsp;</span>
                            <TextField select name={field} variant='outlined' value={benchmarkConfig[field].editDistance} onChange={e => setBenchmarkConfig({ ...benchmarkConfig, [field]: { ...benchmarkConfig[field], editDistance: e.target.value } })} label='Edit Distance' style={{ width: 250 }}>
                                {['0', '1', '2', '3'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                            </TextField>
                            <span>&nbsp;</span>
                            <FormGroup>
                                <FormControlLabel
                                    control={<Checkbox checked={benchmarkConfig[field].spaces} onChange={e => setBenchmarkConfig({ ...benchmarkConfig, [field]: { ...benchmarkConfig[field], spaces: e.target.checked } })} />}
                                    label='Remove Spaces'
                                />
                            </FormGroup>
                        </div>
                    ))}
                </DialogContent>
                <DialogActions style={{ padding: '12px 24px', gap: 8 }}>
                    <HVButton variant="ghost" onClick={() => setOpenBenchmarkConfig(false)}>Cancel</HVButton>
                    <HVButton variant="primary" onClick={handleSetBenchmarkConfig}>Save Config</HVButton>
                </DialogActions>
            </Dialog>

            {/* Set default model dialog */}
            <Dialog open={modelNameOpen} onClose={() => setModelNameOpen(false)} aria-labelledby='set-model' maxWidth='sm' fullWidth>
                <DialogTitle style={{ position: 'relative', paddingRight: 48 }}>
                    Set Default Model
                    <IconButton onClick={() => setModelNameOpen(false)} style={{ position: 'absolute', right: 8, top: 8 }}>
                        <Icons.x size={18} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        name='modelName' variant='outlined' placeholder='model_1' value={modelName}
                        onChange={e => setModelName(e.target.value)} label='Model Name' fullWidth style={{ marginTop: 8 }}
                    />
                </DialogContent>
                <DialogActions style={{ padding: '12px 24px', gap: 8 }}>
                    <HVButton variant="ghost" onClick={() => setModelNameOpen(false)}>Cancel</HVButton>
                    <HVButton variant="primary" onClick={saveModelName}>Save</HVButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default OpenBenchmark;
